import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { listSmsConversations } from '../../services/extension.service';
import ConversationThread from './ConversationThread';
import { Loading } from '../common/Loading';
import { normalizePhoneNumber, getContactDisplayName, getContactInitials } from '../../utils/phone-normalizer';

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const now = new Date();
  const d = new Date(dateStr);
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString();
};

const ConversationsPage = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [filter, setFilter] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkHandled = useRef(false);

  const loadConversations = async () => {
    try {
      const res = await listSmsConversations();
      const list = res.data?.data || [];
      setConversations(list);
      return list;
    } catch (err) {
      console.error('Failed to load conversations:', err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Deep link: /conversations?id=<conversationId> opens that thread directly
    // (e.g. from a notification bell click). Handled once on mount.
    loadConversations().then((list) => {
      if (deepLinkHandled.current) return;
      deepLinkHandled.current = true;
      const targetId = searchParams.get('id');
      if (targetId) {
        const match = list.find((c) => String(c.id) === String(targetId));
        if (match) setSelectedConversation(match);
        // Clear the param so refreshing the page doesn't keep forcing it open
        setSearchParams({}, { replace: true });
      }
    });
    const interval = setInterval(loadConversations, 15000); // poll every 15s
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper: Extract customer name from conversation data
  const getCustomerNameFromConv = (conv) => {
    if (!conv) return null;
    
    // Try customer.firstname/customer.lastname first
    if (conv.customer && (conv.customer.firstname || conv.customer.lastname)) {
      return `${conv.customer.firstname} ${conv.customer.lastname}`.trim();
    }
    
    // Fallback to phone_number as display name
    const phoneNumberStr = String(conv.phone_number || '');
    if (phoneNumberStr) {
      return phoneNumberStr;
    }
    
    return null;
  };

  // Debug logging - show what we're receiving from API
  useEffect(() => {
    if (conversations.length > 0) {
      conversations.slice(0, 3).forEach((conv) => {
        console.log('Conversation loaded:', { 
          id: conv.id,
          phone_number: conv.phone_number,
          customer: conv.customer,
          customerNameFromConv: getCustomerNameFromConv(conv)
        });
      });
    }
  }, [conversations]);

  // Helper: Get formatted phone number for the conversation
  const getFormattedPhoneFromConv = (conv) => {
    if (!conv) return '';
    return normalizePhoneNumber(conv.phone_number) || '';
  };

  // Helper: Extract customer object from conversation data
  const getCustomerObjectFromConv = (conv) => {
    if (!conv) return null;
    
    // Return customer object if it exists, or try to construct one from phone_number
    if (conv.customer) {
      return conv.customer;
    }
    
    // If no customer object, create minimal one with phone_number
    return { phone_number: conv.phone_number };
  };

  const filtered = conversations.filter((conv) => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    
    // Get display name for search using the new helper
    const displayName = getCustomerNameFromConv(conv);
    const name = displayName ? displayName.toLowerCase() : '';
    
    // Also search in raw phone number (before normalization)
    const phoneNumberStr = String(conv.phone_number || '');
    
    // Also search in last message body
    const lastMsgBody = conv.lastMessage?.body || '';

    return (
      name.includes(q) ||
      phoneNumberStr.includes(q) ||
      lastMsgBody.toLowerCase().includes(q)
    );
  });

  if (selectedConversation) {
    // Extract customer name from the selected conversation
    const phoneNumberStr = String(selectedConversation.phone_number || '');
    
    // Try to get customer name from the customer object first
    let customerNameValue = null;
    if (selectedConversation.customer && (selectedConversation.customer.firstname || selectedConversation.customer.lastname)) {
      customerNameValue = `${selectedConversation.customer.firstname} ${selectedConversation.customer.lastname}`.trim();
    }
    
    // If no customer name, fall back to phone number as display name
    if (!customerNameValue && phoneNumberStr) {
      customerNameValue = phoneNumberStr;
    }
    
    return (
      <div className="flex h-full" style={{ height: 'calc(100vh - 120px)' }}>
        <div className="flex-1">
          <ConversationThread
            conversationId={selectedConversation.id}
            phoneNumber={selectedConversation.phone_number}
            customerName={customerNameValue}
            onClose={() => {
              setSelectedConversation(null);
              loadConversations();
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 bg-white shadow-md rounded-md m-2 md:m-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SMS Conversations</h1>
          <p className="text-sm text-gray-500 mt-1">
            {conversations.length} conversation{conversations.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          onClick={loadConversations}
          className="text-sm px-3 py-1.5 rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
        >
          Refresh
        </button>
      </div>

      {conversations.length > 0 && (
        <div className="mb-4">
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search by name, phone, or message…"
            className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><Loading /></div>
      ) : filtered.length === 0 ? (
        <div className="border border-gray-200 rounded-lg p-6 text-center text-gray-500">
          {conversations.length === 0
            ? 'No conversations found.'
            : 'No conversations match your search.'}
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-hidden divide-y divide-gray-100">
          {filtered.map((conv) => {
            // Extract customer name from the customer object if it exists
            const customerNameVal = conv.customer && (conv.customer.firstname || conv.customer.lastname)
              ? `${conv.customer.firstname} ${conv.customer.lastname}`.trim()
              : null;
            
            // If no customer name, use phone number as fallback for display
            const displayFallback = conv.customer && (conv.customer.firstname || conv.customer.lastname)
              ? `${conv.customer.firstname} ${conv.customer.lastname}`.trim()
              : `Phone: ${normalizePhoneNumber(conv.phone_number)}`;
            
            const formattedPhone = normalizePhoneNumber(conv.phone_number);
            const lastMsg = conv.lastMessage;

            // Get initials for avatar
            const avatarInitials = getContactInitials(conv.customer, conv.phone_number);

            return (
              <button
                key={conv.id}
                onClick={() => setSelectedConversation(conv)}
                className="w-full flex items-start gap-3 px-4 py-4 hover:bg-blue-50 transition-colors text-left"
              >
                {/* Avatar */}
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                  <span className="text-blue-700 font-semibold text-lg">
                    {avatarInitials}
                  </span>
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  {/* PRIMARY: Customer Name (bold, larger) */}
                  <p className="font-semibold text-gray-900 truncate leading-tight">
                    {customerNameVal || 'Unknown Contact'}
                  </p>

                  {/* SECONDARY: Phone Number (smaller, muted, Canadian format) */}
                  <p className="text-xs text-gray-400 mt-0.5 leading-none whitespace-normal break-all">
                    {formattedPhone || 'No phone number'}
                  </p>

                  {/* Last Message Preview */}
                  {lastMsg && (
                    <p className="text-sm text-gray-500 mt-2 truncate">
                      <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${
                        lastMsg.direction === 'outbound' ? 'bg-blue-500' : 'bg-green-500'
                      }`} />
                      {lastMsg.body}
                    </p>
                  )}
                </div>

                {/* Timestamp */}
                <div className="shrink-0 mt-1">
                  <span className="text-xs text-gray-400 whitespace-nowrap">
                    {formatRelativeTime(conv.last_message_at)}
                  </span>
                </div>

                {/* Arrow */}
                <div className="text-gray-300 shrink-0 mt-1">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ConversationsPage;
